/**
 * Centralized logging utility for frontend application
 * Provides environment-aware console logging
 * Controlled by VITE_LOG_LEVEL environment variable
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Get log level from environment variable (default to 'info')
const configuredLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 'info';
const isDev = import.meta.env.DEV;

// Log level priority: debug=0, info=1, warn=2, error=3
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Check if a log level should be output based on configured level
 */
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[configuredLevel];
}

/**
 * Format log message with timestamp and context
 */
function formatLogMessage(
  level: string,
  message: string,
  data?: Record<string, unknown>
): string {
  const timestamp = new Date().toISOString();
  const contextStr = data ? ` | ${JSON.stringify(data)}` : '';
  return `[${timestamp}] [${level}] ${message}${contextStr}`;
}

/**
 * Centralized logger with environment-aware behavior
 * Configurable via VITE_LOG_LEVEL: debug, info, warn, error
 */
export const logger = {
  /**
   * Debug-level logging (verbose, development details)
   * Only shown when VITE_LOG_LEVEL=debug
   */
  debug: (message: string, data?: Record<string, unknown>) => {
    if (shouldLog('debug')) {
      console.log(formatLogMessage('DEBUG', message, data));
    }
  },

  /**
   * Info-level logging (normal operations, important events)
   * Shown when VITE_LOG_LEVEL is debug or info
   */
  info: (message: string, data?: Record<string, unknown>) => {
    if (shouldLog('info')) {
      console.log(formatLogMessage('INFO', message, data));
    }
  },

  /**
   * Warning-level logging (non-critical issues)
   * Shown when VITE_LOG_LEVEL is debug, info, or warn
   */
  warn: (message: string, error?: unknown, context?: Record<string, unknown>) => {
    const warnData = error ? {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      ...context,
    } : context;
    
    if (shouldLog('warn')) {
      console.warn(formatLogMessage('WARN', message, warnData));
    }
  },

  /**
   * Error-level logging (critical errors, failures)
   * Always logged regardless of VITE_LOG_LEVEL
   */
  error: (message: string, error?: unknown, context?: Record<string, unknown>) => {
    const errorData = error ? {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      ...context,
    } : context;
    
    if (shouldLog('error')) {
      console.error(formatLogMessage('ERROR', message, errorData));
    }
  },

  /**
   * Set user context (kept for API compatibility)
   * No-op in console-only implementation
   */
  setUserContext: (userId: string, userData?: Record<string, unknown>) => {
    if (isDev && shouldLog('debug')) {
      console.log(formatLogMessage('USER_CONTEXT', `User: ${userId}`, userData));
    }
  },

  /**
   * Log user action for debugging
   */
  logAction: (action: string, data?: Record<string, unknown>) => {
    if (shouldLog('debug')) {
      console.log(formatLogMessage('ACTION', action, data));
    }
  },
};
